import { useRef } from 'react';
import { CmsSectionShell } from './CmsSectionShell.tsx';
import { SectionHeading } from '../SectionHeading.tsx';
import { themeOf, OUTLINE_CONTROL } from './cmsTheme.ts';
import { cdnSrcSet, cdnVariant } from '../../lib/cdn.ts';
import type { GallerySection } from '../../lib/types.ts';

/**
 * Horizontal photo carousel (CMS-031): a row of images the visitor can scroll/swipe through.
 * `MapSection` generalized to many images — the images are authored directly on the section
 * (Media Library), not sourced from festival data. Omitted entirely when fewer than two images
 * have been authored.
 *
 * In v2 the nav buttons moved out of the carousel and into the header row beside the heading, as
 * quiet outline circles: floating coral discs over the photos read as the loudest thing in the
 * section, which they are not.
 */
export function CmsGallerySection({ theme, guitar, heading, images }: GallerySection) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const items = images ?? [];
  if (items.length < 2) return null;

  const t = themeOf(theme);
  const arrowClass = `inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors ${OUTLINE_CONTROL[t.outline]}`;

  function scrollByCard(direction: 1 | -1) {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-gallery-card]');
    const amount = (card?.offsetWidth ?? el.clientWidth * 0.8) + 16;
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      <div className="mb-6 md:mb-8 flex items-end justify-between gap-6">
        {heading
          ? <SectionHeading color={t.heading} eyebrowColor={t.eyebrow} className="mb-0">{heading}</SectionHeading>
          : <span />}
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => scrollByCard(-1)} aria-label="Forrige billeder" className={arrowClass}>
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M19 12H5" />
              <path d="M11 6l-6 6 6 6" />
            </svg>
          </button>
          <button type="button" onClick={() => scrollByCard(1)} aria-label="Næste billeder" className={arrowClass}>
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14" />
              <path d="M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 scrollbar-none [&::-webkit-scrollbar]:hidden"
      >
        {items.map((image, i) => (
          <div
            key={`${image.src}-${i}`}
            data-gallery-card
            // Desktop: exactly 3 cards fill the row (minus the two gap-4 gaps) so the last page
            // aligns cleanly at max scroll.
            className={`shrink-0 w-[75%] sm:w-[45%] md:w-[calc((100%-2rem)/3)] aspect-3/2 snap-start rounded-card overflow-hidden ${t.img}`}
          >
            <img
              src={cdnSrcSet(image.src) ? cdnVariant(image.src, 'mobile') : image.src}
              srcSet={cdnSrcSet(image.src) ?? undefined}
              sizes="(min-width: 768px) 32vw, (min-width: 640px) 45vw, 75vw"
              alt={image.alt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </CmsSectionShell>
  );
}
